import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Send, MessageSquare, CheckCheck, Clock, HelpCircle,
  ChevronDown, ChevronUp, Pin, Search, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const InboxPage = ({ user }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [mentor, setMentor] = useState(null);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Q&A state
  const [questions, setQuestions] = useState([]);
  const [showAskModal, setShowAskModal] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [expandedQ, setExpandedQ] = useState({});
  
  // FAQ state
  const [faqs, setFaqs] = useState([]);
  const [expandedFaq, setExpandedFaq] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const fetchAll = useCallback(async () => {
    try {
      const [msgRes, qRes, faqRes] = await Promise.all([
        fetch(`${API}/api/inbox/my-messages`, { headers: hdr() }),
        fetch(`${API}/api/helpdesk/questions?filter=all`, { headers: hdr() }),
        fetch(`${API}/api/faq`, { headers: hdr() })
      ]);
      if (msgRes.ok) { const d = await msgRes.json(); setMessages(d.messages || []); setMentor(d.mentor || null); }
      if (qRes.ok) setQuestions(await qRes.json());
      if (faqRes.ok) setFaqs(await faqRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendMessage = async () => {
    if (!newMsg.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/inbox/send`, { method: 'POST', headers: hdr(), body: JSON.stringify({ content: newMsg.trim() }) });
      if (res.ok) { setNewMsg(''); fetchAll(); }
      else { const d = await res.json(); toast.error(d.detail || 'Failed'); }
    } catch (e) { toast.error('Failed to send'); }
    setSending(false);
  };

  const submitQuestion = async () => {
    if (!newQuestion.trim()) { toast.error('Write your question'); return; }
    try {
      const res = await fetch(`${API}/api/helpdesk/questions`, { method: 'POST', headers: hdr(), body: JSON.stringify({ question: newQuestion, category: 'general' }) });
      if (res.ok) { toast.success('Question sent! Your mentor will respond soon.'); setShowAskModal(false); setNewQuestion(''); fetchAll(); }
    } catch (e) { toast.error('Failed'); }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((now - d) / 86400000);
    if (diffDays === 0) return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return d.toLocaleDateString('en-US', { weekday: 'short' });
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const filteredFaqs = faqs.filter(f => !searchTerm || f.question?.toLowerCase().includes(searchTerm.toLowerCase()) || f.answer?.toLowerCase().includes(searchTerm.toLowerCase()));

  const TABS = [
    { id: 'messages', label: 'Messages', icon: MessageSquare, count: messages.length },
    { id: 'qa', label: 'Q&A', icon: HelpCircle, count: questions.filter(q => q.status === 'answered').length },
    { id: 'faq', label: 'FAQ', icon: Search, count: faqs.length }
  ];

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#F5F3EF' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #1a1f3a 100%)' }} className="px-4 pt-4 pb-2">
        <div className="max-w-lg mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-xl bg-white/10 hover:bg-white/15" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 text-white/70" />
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-white">Inbox</h1>
              {mentor && tab === 'messages' && <p className="text-xs text-white/50">with {mentor.name}</p>}
            </div>
          </div>
          
          {/* Tab bar */}
          <div className="flex gap-1 bg-white/10 rounded-xl p-1">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex-1 py-2 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 ${tab === t.id ? 'bg-white text-gray-900' : 'text-white/60 hover:text-white/80'}`}
                data-testid={`tab-${t.id}`}>
                <t.icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ==================== MESSAGES TAB ==================== */}
      {tab === 'messages' && (
        <>
          <div className="flex-1 overflow-y-auto">
            <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
              {loading ? (
                <p className="text-center text-gray-400 py-8">Loading...</p>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm font-medium">No messages yet</p>
                  <p className="text-gray-400 text-xs mt-1">
                    {mentor ? `Start a conversation with ${mentor.name}` : 'Your mentor will be assigned soon'}
                  </p>
                </div>
              ) : (
                messages.map((msg, i) => {
                  const isMe = msg.from_id === user.id;
                  return (
                    <motion.div key={msg.id || i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] p-3 rounded-2xl ${isMe
                        ? 'bg-orange-500 text-white rounded-br-sm'
                        : 'bg-white text-gray-800 border border-gray-100 shadow-sm rounded-bl-sm'}`}>
                        {!isMe && <p className="text-[10px] font-bold text-orange-500 mb-1">{msg.from_name}</p>}
                        <p className="text-sm leading-relaxed">{msg.content}</p>
                        <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : ''}`}>
                          <span className={`text-[9px] ${isMe ? 'text-white/60' : 'text-gray-400'}`}>{formatTime(msg.created_at)}</span>
                          {isMe && <CheckCheck className="w-3 h-3 text-white/60" />}
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          </div>
          {/* Compose */}
          {mentor && (
            <div className="border-t border-gray-200 bg-white px-4 py-3">
              <div className="max-w-lg mx-auto flex gap-2">
                <Textarea value={newMsg} onChange={e => setNewMsg(e.target.value)}
                  placeholder={`Message ${mentor.name}...`}
                  rows={1} className="flex-1 rounded-xl resize-none border-gray-200 text-sm min-h-[40px] max-h-[120px]"
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }}}
                  data-testid="message-input" />
                <Button onClick={sendMessage} disabled={sending || !newMsg.trim()}
                  className="rounded-xl bg-orange-500 hover:bg-orange-600 px-4 self-end" data-testid="send-message-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ==================== Q&A TAB ==================== */}
      {tab === 'qa' && (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
            {/* Ask button */}
            <Button onClick={() => setShowAskModal(true)} className="w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold py-5" data-testid="ask-question-btn">
              <Plus className="w-4 h-4 mr-2" /> Ask Your {user?.staff_role_label || 'Mentor'} a Question
            </Button>

            <p className="text-[10px] text-gray-400 text-center">Questions are visible to your cohort so everyone benefits from the answer</p>

            {/* Questions list */}
            {questions.length === 0 ? (
              <div className="text-center py-8">
                <HelpCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">No questions yet. Be the first to ask!</p>
              </div>
            ) : (
              questions.map(q => (
                <Card key={q.id} className="rounded-2xl overflow-hidden border border-gray-100" data-testid={`question-${q.id}`}>
                  <button onClick={() => setExpandedQ(prev => ({ ...prev, [q.id]: !prev[q.id] }))}
                    className="w-full p-4 text-left flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${q.status === 'answered' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                      {q.status === 'answered' ? <CheckCheck className="w-4 h-4 text-emerald-600" /> : <Clock className="w-4 h-4 text-amber-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800">{q.question}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">
                        {q.asked_by_name} · {formatTime(q.created_at)}
                        {q.is_pinned && <Pin className="w-3 h-3 inline ml-1 text-orange-400" />}
                      </p>
                    </div>
                    {expandedQ[q.id] ? <ChevronUp className="w-4 h-4 text-gray-400 mt-1" /> : <ChevronDown className="w-4 h-4 text-gray-400 mt-1" />}
                  </button>
                  {expandedQ[q.id] && q.answer && (
                    <div className="px-4 pb-4">
                      <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-100 ml-11">
                        <p className="text-[10px] font-bold text-emerald-700 mb-1">{q.answered_by_name}</p>
                        <p className="text-sm text-gray-700">{q.answer}</p>
                        <p className="text-[9px] text-gray-400 mt-1">{formatTime(q.answered_at)}</p>
                      </div>
                    </div>
                  )}
                  {expandedQ[q.id] && !q.answer && (
                    <div className="px-4 pb-4 ml-11">
                      <p className="text-xs text-amber-600 italic">Waiting for your {user?.staff_role_label?.toLowerCase() || 'mentor'} to respond...</p>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>

          {/* Ask Question Modal */}
          <Dialog open={showAskModal} onOpenChange={setShowAskModal}>
            <DialogContent className="rounded-2xl max-w-md">
              <DialogHeader><DialogTitle>Ask Your {user?.staff_role_label || 'Mentor'}</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-2">
                <p className="text-xs text-gray-500">Check the FAQ tab first — your answer might already be there!</p>
                <Textarea value={newQuestion} onChange={e => setNewQuestion(e.target.value)}
                  placeholder="What do you want to know?" rows={3} className="rounded-xl" data-testid="question-input" />
                <Button onClick={submitQuestion} className="w-full rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid="submit-question-btn">
                  Send Question
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ==================== FAQ TAB ==================== */}
      {tab === 'faq' && (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
              <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search common questions..."
                className="pl-10 rounded-xl bg-white border-gray-200" data-testid="faq-search" />
            </div>

            {filteredFaqs.length === 0 ? (
              <div className="text-center py-8">
                <Search className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-400">{searchTerm ? 'No matching questions' : 'No FAQs posted yet'}</p>
              </div>
            ) : (
              filteredFaqs.map(faq => (
                <Card key={faq.id} className="rounded-2xl overflow-hidden border border-gray-100">
                  <button onClick={() => setExpandedFaq(prev => ({ ...prev, [faq.id]: !prev[faq.id] }))}
                    className="w-full p-4 text-left flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <HelpCircle className="w-4 h-4 text-blue-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-800 flex-1">{faq.question}</p>
                    {expandedFaq[faq.id] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </button>
                  {expandedFaq[faq.id] && (
                    <div className="px-4 pb-4 ml-11">
                      <p className="text-sm text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </Card>
              ))
            )}

            <p className="text-center text-[10px] text-gray-400 pt-4">
              Can not find your answer? Switch to the Q&A tab and ask your {user?.staff_role_label?.toLowerCase() || 'mentor'} directly.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default InboxPage;
